import Konva from "konva";
import { KonvaNodeComponent } from "react-konva";
import {
  getSnappingGuidelines,
  getSnapLinesForPoint,
  rotate,
  getAnchorPos,
  isCorner,
  checkTransformSnapLines,
  getSizeSnapLines,
  AllSnapCandidates,
  renderGuidelinesDirectly,
  clearGuidelines,
} from "frontend/utils/snap-utils";
import { isMac } from "frontend/utils/keyboard-shortcuts";
import consts from "shared/consts";
import { getRotatedRectCenter, stageToViewport, viewportToStage } from "frontend/utils/math-utils";
import { Box } from "konva/types/shapes/Transformer";
import { roundBox, roundToPrecision, safeDivide } from "utils/transform";
import { equals } from "rambda";

// Konva's built-in rotation function from Transformer source
function rotateAroundPoint(shape: Box, angleRad: number, point: { x: number; y: number }) {
  const x = point.x + (shape.x - point.x) * Math.cos(angleRad) - (shape.y - point.y) * Math.sin(angleRad);
  const y = point.y + (shape.x - point.x) * Math.sin(angleRad) + (shape.y - point.y) * Math.cos(angleRad);
  return {
    ...shape,
    rotation: shape.rotation + angleRad,
    x,
    y,
  };
}

const MinimumWidth = 30;
const MinimumHeight = 30;

const PI_UNDER_180 = 180 / Math.PI;
// Return angle from Konva's internal to degrees radians
function getKonvaAngleAsDegrees(angle: number) {
  return Konva.angleDeg ? angle : angle * PI_UNDER_180;
}

// this is the order or compass points, starting from north, clockwise
const cursorTypeByCompassPoint = [
  "ns-resize",
  "nesw-resize",
  "ew-resize",
  "nwse-resize",
  "ns-resize",
  "nesw-resize",
  "ew-resize",
  "nwse-resize",
];

const AnchorToAngle: Record<string, number> = {
  "top-left": -45,
  "top-center": 0,
  "top-right": 45,
  "middle-right": 90,
  "middle-left": -90,
  "bottom-left": -135,
  "bottom-center": 180,
  "bottom-right": 135,
};

function getCursor(anchorName: string, deg: number) {
  deg += AnchorToAngle[anchorName] || 0; // add anchor base rotation to current rotation;
  deg = ((deg % 360) + 360) % 360; // clamp to 0..360 range
  // now check to which of the 8 compass points deg is closest to. each point gives different cursor
  deg += 22.5;
  deg = Math.floor(deg / 45);
  return cursorTypeByCompassPoint[deg % cursorTypeByCompassPoint.length];
}

export type AnchorDblClickCb = (e: { evt: MouseEvent; transformer: SnappingTransformer; nodes: Konva.Node[] }) => void;

interface SnappingTransformerProps extends Konva.TransformerConfig {
  newTransformerDesign: boolean;
  showSnapGuidelines: boolean;
  snapToGuidelines: boolean;
  anchorDblClick?: AnchorDblClickCb;
  setResizingElements?: (resizing: boolean) => void;
}

class SnappingTransformer extends Konva.Transformer {
  private centered = false;
  private disableSnapping = false;
  private keepAspectRatio = false;
  private lockRotationToIntervals = false;
  private ratio = 1;
  // @ts-expect-error Konva types are mismatched here. This works.
  private elementsLayer;
  private overlayLayer: Konva.Layer | null = null;
  private snapData: AllSnapCandidates | null = null;
  private elementTypes: string[] = [];
  private uniformType: string | null = null;
  private rotater: Konva.Rect;
  private rTopLeft?: Konva.Group;
  private rTopRight?: Konva.Group;
  private rBottomLeft?: Konva.Group;
  private rBottomRight?: Konva.Group;
  private isRotating = false;
  private currentRotationCorner: string | null = null;
  private currentCursor: string = "";

  constructor(config: SnappingTransformerProps) {
    super(config);

    const setMouseCursorOnTransform = () => {
      this.setCursor(this.getStage()!, this.currentCursor);
    };

    this.on("transformstart", () => {
      this.ratio = this.width() / this.height();
      const layers = this.getStage()?.getLayers().toArray();
      this.elementsLayer = layers?.find((x) => x.name() == "Elements"); // this can probably be cached, since layers don't change
      this.overlayLayer = (layers?.find((x) => x.name() == "Overlay") as Konva.Layer) || null;
      const selectedNodes = [];
      // @ts-expect-error Konva types are mismatched here.
      for (const node_ of this.nodes()) {
        let node = node_;
        while (node) {
          if (node.getParent()?.nodeType == "Layer") break;
          node = node.getParent();
        }
        // while (node.getParent().getClassName() != "Layer") node = node.getParent();
        selectedNodes.push(node);
      }
      this.snapData = getSnappingGuidelines(this.elementsLayer, selectedNodes);
      window.dispatchEvent(new CustomEvent("transform-start", { detail: { nodes: selectedNodes } }));
    });

    this.on("transform", (e) => {
      if (e.evt) {
        this.disableSnapping = isMac() ? e.evt.metaKey : e.evt.ctrlKey;
        this.centered = e.evt.altKey;
        this.keepAspectRatio = e.evt.shiftKey;
        this.lockRotationToIntervals = e.evt.shiftKey;
      }
      window.dispatchEvent(new CustomEvent("transform", { detail: {} }));
    });

    this.on("transformend", () => {
      this.clearCursor();
      clearGuidelines(this.overlayLayer, "transformer-snap-guide");
      this.updateCustomRotationHandle();
      window.dispatchEvent(new CustomEvent("transform-end", { detail: {} }));
      window.removeEventListener("mousemove", setMouseCursorOnTransform);
    });

    const callback = this.anchorDblClick.bind(this);
    for (const anchor of this.find("._anchor")) {
      anchor.on("dblclick", callback);
      anchor.on("mousedown", () => {
        const angle = getKonvaAngleAsDegrees(this.rotation());
        this.currentCursor = getCursor(this.getActiveAnchor(), angle);
        setMouseCursorOnTransform();
        if (!this.isRotating) window.addEventListener("mousemove", setMouseCursorOnTransform);
      });
    }

    this.rotater = this.findOne(".rotater");
    this.rotater.off("mouseenter");
    this.rotater.on("mouseenter", (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Don't change cursor if any mouse buttons are pressed (during drag operations)
      if (e.evt.buttons === 0) {
        const stage = this.rotater.getStage();
        if (stage) {
          this.setCursor(
            stage,
            `url("data:image/svg+xml,%3Csvg width='14' height='15' viewBox='0 0 14 15' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg filter='url(%23filter0_d_78_1192)'%3E%3Cpath d='M6.4 3.4C6.95155 3.4 7.4977 3.50864 8.00727 3.71971C8.51684 3.93078 8.97984 4.24015 9.36985 4.63015C9.75986 5.02016 10.0692 5.48316 10.2803 5.99273C10.4914 6.5023 10.6 7.04845 10.6 7.6V9.4H13L9.4 13L5.8 9.4H8.2V7.6C8.2 7.36362 8.15344 7.12956 8.06298 6.91117C7.97253 6.69278 7.83994 6.49435 7.67279 6.32721C7.50565 6.16006 7.30722 6.02748 7.08883 5.93702C6.87044 5.84656 6.63638 5.8 6.4 5.8H4.6V8.2L1 4.6L4.6 1V3.4H6.4Z' fill='white'/%3E%3Cpath d='M6.39999 5.2H3.99999V6.7L1.89999 4.6L4 2.5L3.99999 4H6.39999C6.87275 4 7.34088 4.09312 7.77766 4.27403C8.21443 4.45495 8.61129 4.72012 8.94558 5.05441C9.27987 5.38871 9.54505 5.78557 9.72596 6.22234C9.90688 6.65911 10 7.12724 10 7.6V10L11.5 10L9.4 12.1L7.3 10H8.8V7.6C8.8 7.28483 8.73792 6.97274 8.61731 6.68156C8.4967 6.39038 8.31991 6.1258 8.09705 5.90294C7.87419 5.68008 7.60962 5.5033 7.31844 5.38269C7.02725 5.26208 6.71517 5.2 6.39999 5.2Z' fill='black'/%3E%3C/g%3E%3Cdefs%3E%3Cfilter id='filter0_d_78_1192' x='0' y='0.5' width='14' height='14' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/%3E%3CfeOffset dy='0.5'/%3E%3CfeGaussianBlur stdDeviation='0.5'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.65 0'/%3E%3CfeBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_78_1192'/%3E%3CfeBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_78_1192' result='shape'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E%0A") 7 7, auto`
          );
          this._cursorChange = true;
        }
      }
    });
    this.createContinuousCornerRotationZones();
    const snapper = this.boundBoxFuncWithSnap.bind(this);
    this.rotationSnaps([0, 45, 90, 135, 180, 225, 270, 315]);
    this.boundBoxFunc(snapper);
  }

  anchorDblClick(e: any) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore -> No error on build, only in editor.
    this.attrs.anchorDblClick && this.attrs.anchorDblClick({ transformer: this, evt: e.evt, nodes: this.nodes() });
  }

  shouldShowGuidelines() {
    return this.attrs.showSnapGuidelines;
  }

  shouldSnap() {
    return this.disableSnapping == false && this.shouldShowGuidelines() && this.attrs.snapToGuidelines;
  }

  // @ts-expect-error This will work.
  nodes(args?: Konva.Node[]) {
    if (args == undefined) {
      /* eslint-disable @typescript-eslint/ban-ts-comment */
      // @ts-ignore -> No error on build, only in editor.
      return super.nodes();
    }

    if (equals(args, super.getNodes())) {
      return;
    }

    this.elementTypes = args.map((node: any) => node.attrs.element.type);
    this.uniformType = this.elementTypes.every((x) => x == this.elementTypes[0]) ? this.elementTypes[0] : null;
    // @ts-ignore -> No error on build, only in editor.
    return super.nodes(args);
  }

  update() {
    super.update();

    this.styleAnchorsManually();

    if (!this.attrs.newTransformerDesign) {
      this.rotater.cornerRadius(this.rotater.width() / 2);
      this.rotater.hitStrokeWidth(10);
      return;
    }

    // Hide the default rotation handle and line
    this.hideDefaultRotationElements();

    // Add custom rotation handle using a simple approach
    this.updateCustomRotationHandle();

    // Override the transformer border to remove rotation line segment
    this.customizeTransformerBorder();
  }

  private hideDefaultRotationElements() {
    // Hide the default rotater handle directly
    if (this.rotater) {
      this.rotater.visible(false);
      this.rotater.listening(false);
    }
  }

  private styleAnchorsManually() {
    // Style corner anchors with custom appearance and perfect corner alignment
    const attrs = this._getNodeRect();
    const width = attrs.width;
    const height = attrs.height;
    const anchorSize = 8.5;

    const baseCornerConfig = {
      width: anchorSize,
      height: anchorSize,
      offsetX: anchorSize / 2, // Center the anchor on the corner intersection
      offsetY: anchorSize / 2,
      fill: this.anchorFill(),
      stroke: this.anchorStroke(),
      strokeWidth: 1.5,
      cornerRadius: 2.25,
    };

    this._batchChangeChild(".top-left", {
      ...baseCornerConfig,
      x: 0,
      y: 0,
    });

    this._batchChangeChild(".top-right", {
      ...baseCornerConfig,
      x: width,
      y: 0,
    });

    this._batchChangeChild(".bottom-left", {
      ...baseCornerConfig,
      x: 0,
      y: height,
    });

    this._batchChangeChild(".bottom-right", {
      ...baseCornerConfig,
      x: width,
      y: height,
    });

    // Style middle anchors as edge resize handles
    const resizeEnabled = this.resizeEnabled();
    const enabledAnchors = this.enabledAnchors();

    // Base configuration for edge/middle anchors
    const baseEdgeConfig = {
      stroke: this.anchorStroke(),
      strokeWidth: 0.5,
      hitStrokeWidth: 4,
    };

    this._batchChangeChild(".middle-left", {
      ...baseEdgeConfig,
      x: 0,
      y: height / 2,
      width: 0.5,
      height: height - anchorSize,
      offsetX: 0.5,
      offsetY: (height - anchorSize) / 2,
      visible: resizeEnabled && enabledAnchors.includes("middle-left"),
    });

    this._batchChangeChild(".middle-right", {
      ...baseEdgeConfig,
      x: width,
      y: height / 2,
      width: 0.5,
      height: height - anchorSize,
      offsetX: 0.5,
      offsetY: (height - anchorSize) / 2,
      visible: resizeEnabled && enabledAnchors.includes("middle-right"),
    });

    this._batchChangeChild(".top-center", {
      ...baseEdgeConfig,
      x: width / 2,
      y: 0,
      width: width - anchorSize,
      height: 0.5,
      offsetX: (width - anchorSize) / 2,
      offsetY: 0.5,
      visible: resizeEnabled && enabledAnchors.includes("top-center"),
    });

    this._batchChangeChild(".bottom-center", {
      ...baseEdgeConfig,
      x: width / 2,
      y: height,
      width: width - anchorSize,
      height: 0.5,
      offsetX: (width - anchorSize) / 2,
      offsetY: 0.5,
      visible: resizeEnabled && enabledAnchors.includes("bottom-center"),
    });
  }

  // Custom rotation handler that works with our corner rotation zones
  private handleCustomRotation = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;

    const stage = this.getStage();
    if (!stage) return;
    const initialPos = stage.getPointerPosition();
    if (!initialPos) return;

    // Set rotation mode and cursor using the corner that initiated rotation
    this.isRotating = true;

    // Disable all anchor listening during rotation to prevent cursor changes
    for (const anchor of this.find("._anchor")) {
      anchor.listening(false);
    }

    this.setRotationCursor(this.currentRotationCorner || undefined);

    // Get initial state
    const attrs = this._getNodeRect();

    // Get the transformer's current rotation
    const transformerRotation = this.rotation() * (Math.PI / 180); // Convert to radians

    // Calculate the actual center point of the rotated transformer
    const center = getRotatedRectCenter(attrs, transformerRotation);
    const centerX = center.x;
    const centerY = center.y;

    // Store initial rotation
    const startRotation = attrs.rotation;
    const mouseAngle = Math.atan2(initialPos.y - centerY, initialPos.x - centerX);

    // Ensure nodes is always an array
    const nodes = this.nodes();
    const nodeArray = Array.isArray(nodes) ? nodes : [];

    // Get all nodes initial rotations and positions
    const initialRotations = nodeArray.map((node) => node.rotation());
    const initialPositions = nodeArray.map((node) => node.getAbsolutePosition());

    // Mark as transforming
    this._transforming = true;
    this._movingAnchorName = "rotater";
    // Fire transformstart
    this.fire("transformstart", { evt: e.evt, target: this.getNode() });
    for (const target of nodeArray) {
      target._fire("transformstart", { evt: e.evt, target });
    }

    // Use pure incremental tracking for all cases
    let lastMouseAngle = mouseAngle;
    let totalRotation = 0;

    const handleMouseMove = (moveEvent: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      const pos = stage.getPointerPosition();
      if (!pos) return;

      // Calculate current mouse angle
      const currentMouseAngle = Math.atan2(pos.y - centerY, pos.x - centerX);

      // Calculate incremental change since last mouse move
      let incrementalDelta = currentMouseAngle - lastMouseAngle;

      // Handle angle wrapping for incremental changes
      if (incrementalDelta > Math.PI) {
        incrementalDelta -= 2 * Math.PI;
      } else if (incrementalDelta < -Math.PI) {
        incrementalDelta += 2 * Math.PI;
      }

      // Accumulate the total rotation
      totalRotation += incrementalDelta;

      // Apply rotation snapping with smooth transitions
      let snappedTotalRotation = totalRotation;

      if (this.rotationSnaps().length > 0) {
        const currentRotation = startRotation + totalRotation;
        const tol = (5 * Math.PI) / 180; // Force 5 degrees tolerance
        const snaps = this.rotationSnaps().map((s) => (s * Math.PI) / 180);

        let closestSnap = null;
        let minDistance = Number.POSITIVE_INFINITY;

        for (const snap of snaps) {
          // Calculate distance considering 360° wrapping
          let diff = Math.abs(currentRotation - snap) % (Math.PI * 2);
          diff = Math.min(diff, Math.PI * 2 - diff);

          if (diff < tol && diff < minDistance) {
            closestSnap = snap;
            minDistance = diff;
          }
        }

        if (closestSnap !== null) {
          // Smooth snapping - adjust only the output rotation, not the tracking
          snappedTotalRotation = closestSnap - startRotation;
          // DON'T update totalRotation - let it continue accumulating naturally
          // This keeps mouse tracking working for slow movements
        }
      }

      // Update mouse tracking after snapping calculations
      lastMouseAngle = currentMouseAngle;

      // Update rotation cursor during rotation with current angle
      if (this.isRotating) {
        // Calculate the current total rotation in degrees
        const currentRotationDegrees = ((startRotation + snappedTotalRotation) * 180) / Math.PI;
        this.setDynamicRotationCursor(this.currentRotationCorner || undefined, currentRotationDegrees);
      }

      // Apply rotation to all nodes
      const rotationDeltaRadians = snappedTotalRotation;

      // Apply rotation using Konva's built-in rotation logic
      for (const [index, node] of nodeArray.entries()) {
        const initialPos = initialPositions[index];

        // Create a Box representation of the node's initial state
        const nodeBox: Box = {
          x: initialPos.x,
          y: initialPos.y,
          width: node.width() * (node.scaleX() || 1),
          height: node.height() * (node.scaleY() || 1),
          rotation: (initialRotations[index] * Math.PI) / 180, // Convert to radians
        };

        // Use Konva's rotateAroundPoint function with our calculated center
        const rotationCenter = { x: centerX, y: centerY };
        const newBox = rotateAroundPoint(nodeBox, rotationDeltaRadians, rotationCenter);

        // Apply the results back to the node
        node.rotation((newBox.rotation * 180) / Math.PI); // Convert back to degrees
        node.setAbsolutePosition({ x: newBox.x, y: newBox.y });
      }

      // Always rotate the transformer to show visual feedback during rotation
      // Use the snapped rotation for consistent visual feedback
      this.rotation(((startRotation + snappedTotalRotation) * 180) / Math.PI);

      // Fire transform event
      this._fire("transform", { evt: moveEvent, target: this.getNode() });
      for (const target of this._nodes) {
        target._fire("transform", { evt: moveEvent, target });
      }
    };

    const handleMouseUp = (upEvent: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      stage.off("mousemove", handleMouseMove);
      stage.off("mouseup", handleMouseUp);
      stage.off("touchmove", handleMouseMove);
      stage.off("touchend", handleMouseUp);

      // Mark as not transforming and not rotating
      this._transforming = false;
      this._movingAnchorName = "";
      this.isRotating = false;
      this.currentRotationCorner = null;

      // Re-enable all anchor listening after rotation
      for (const anchor of this.find("._anchor")) {
        anchor.listening(true);
      }

      // Clear cursor
      this.clearCursor();

      // Fire transformend event
      this._fire("transformend", { evt: upEvent, target: this.getNode() });
      for (const target of this._nodes) {
        target._fire("transformend", { evt: upEvent, target });
      }
    };

    // Attach event handlers
    stage.on("mousemove", handleMouseMove);
    stage.on("mouseup", handleMouseUp);
    stage.on("touchmove", handleMouseMove);
    stage.on("touchend", handleMouseUp);
  };

  private setCursor(stage: Konva.Stage, cursor: string) {
    stage.content.style.cursor = cursor;
  }

  private updateCustomRotationHandle() {
    // Only add rotation zones if rotation is enabled
    const enable = this.rotateEnabled();
    this.rTopLeft?.listening(enable);
    this.rTopRight?.listening(enable);
    this.rBottomLeft?.listening(enable);
    this.rBottomRight?.listening(enable);
    if (enable) {
      const attrs = this._getNodeRect();
      this.rTopRight?.x(attrs.width);
      this.rBottomLeft?.y(attrs.height);
      this.rBottomRight?.x(attrs.width);
      this.rBottomRight?.y(attrs.height);
    }
  }

  private createSingleCornerRotator(name: string, rotation = 0) {
    const cornerRotator = new Konva.Group({
      name,
      x: 0,
      y: 0,
      listening: true,
      rotationDeg: rotation,
    });
    cornerRotator.add(
      new Konva.Rect({
        name: "anchor-rotation",
        x: -15,
        y: -15,
        width: 20,
        height: 10,
        fill: "transparent",
      })
    );
    cornerRotator.add(
      new Konva.Rect({
        name: "anchor-rotation",
        x: -15,
        y: -15,
        width: 10,
        height: 20,
        fill: "transparent",
      })
    );
    return cornerRotator;
  }

  private createContinuousCornerRotationZones() {
    this.rTopLeft = this.createSingleCornerRotator("anchor-rotation-zone-top-left", 0);
    this.rTopRight = this.createSingleCornerRotator("anchor-rotation-zone-top-right", 90);
    this.rBottomLeft = this.createSingleCornerRotator("anchor-rotation-zone-bottom-left", 270);
    this.rBottomRight = this.createSingleCornerRotator("anchor-rotation-zone-bottom-right", 180);

    const onMouseEnter = (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Don't change cursor if any mouse buttons are pressed (during drag operations)
      if (e.evt.buttons === 0) {
        const corner = e.currentTarget;
        const attrs = this._getNodeRect();
        const visualCorner = this.determineVisualCorner(corner.x(), corner.y(), attrs);
        this.setRotationCursor(visualCorner);
      }
    };
    const onMouseLeave = (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Don't change cursor if any mouse buttons are pressed (during drag operations)
      if (e.evt.buttons === 0) {
        this.clearCursor();
      }
    };
    const onMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
      const corner = e.currentTarget;
      const attrs = this._getNodeRect();
      const visualCorner = this.determineVisualCorner(corner.x(), corner.y(), attrs);
      this.currentRotationCorner = visualCorner;
      this.handleCustomRotation(e);
    };

    this.rTopLeft.on("mouseenter", onMouseEnter);
    this.rTopLeft.on("mouseleave", onMouseLeave);
    this.rTopLeft.on("mousedown", onMouseDown);

    this.rTopRight.on("mouseenter", onMouseEnter);
    this.rTopRight.on("mouseleave", onMouseLeave);
    this.rTopRight.on("mousedown", onMouseDown);

    this.rBottomLeft.on("mouseenter", onMouseEnter);
    this.rBottomLeft.on("mouseleave", onMouseLeave);
    this.rBottomLeft.on("mousedown", onMouseDown);

    this.rBottomRight.on("mouseenter", onMouseEnter);
    this.rBottomRight.on("mouseleave", onMouseLeave);
    this.rBottomRight.on("mousedown", onMouseDown);

    this.add(this.rTopLeft);
    this.add(this.rTopRight);
    this.add(this.rBottomLeft);
    this.add(this.rBottomRight);
  }

  private determineVisualCorner(anchorX: number, anchorY: number, attrs: any): string {
    // Determine which visual corner this position represents based on current bounds
    const isLeft = anchorX <= attrs.width / 2;
    const isTop = anchorY <= attrs.height / 2;

    if (isTop && !isLeft) {
      return "top-right";
    } else if (!isTop && !isLeft) {
      return "bottom-right";
    } else if (!isTop && isLeft) {
      return "bottom-left";
    } else {
      return "top-left";
    }
  }

  private getCurrentShapeRotation(): number {
    // Get the current rotation of the shape in degrees
    const nodes = this.nodes();
    let nodeArray: any[] = [];
    if (Array.isArray(nodes)) {
      nodeArray = nodes;
    }

    if (nodeArray.length > 0) {
      return this.rotation() || 0;
    }
    return 0;
  }

  private setRotationCursor(corner?: string) {
    const stage = this.getStage();
    if (stage) {
      const currentRotation = this.getCurrentShapeRotation();
      const cursorUrl = this.getRotationCursorForCorner(corner || "top-right", currentRotation);
      this.setCursor(stage, `url("${cursorUrl}") 12 12, auto`);
    }
  }

  private setDynamicRotationCursor(corner?: string, currentRotationDegrees?: number) {
    const stage = this.getStage();
    if (stage) {
      // Use the provided rotation or get current shape rotation
      const rotation = currentRotationDegrees === undefined ? this.getCurrentShapeRotation() : currentRotationDegrees;
      const cursorUrl = this.getRotationCursorForCorner(corner || "top-right", rotation);
      this.setCursor(stage, `url("${cursorUrl}") 12 12, auto`);
    }
  }

  private getRotationCursorForCorner(corner: string, shapeRotationDegrees: number = 0): string {
    // Get base rotation for the corner
    let baseRotation = 0;
    switch (corner) {
      case "top-right": {
        baseRotation = 0; // Right-pointing arrow
        break;
      }
      case "bottom-right": {
        baseRotation = 90; // Down-pointing arrow
        break;
      }
      case "bottom-left": {
        baseRotation = 180; // Left-pointing arrow
        break;
      }
      case "top-left": {
        baseRotation = 270; // Up-pointing arrow
        break;
      }
    }

    // Add the shape's rotation to the base rotation
    const totalRotation = baseRotation + shapeRotationDegrees;
    return `data:image/svg+xml,%3Csvg width='24' height='25' viewBox='0 0 24 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg transform='rotate(${totalRotation} 12 12.5) scale(1.5)'%3E%3Cpath d='M6.4 3.4C6.95155 3.4 7.4977 3.50864 8.00727 3.71971C8.51684 3.93078 8.97984 4.24015 9.36985 4.63015C9.75986 5.02016 10.0692 5.48316 10.2803 5.99273C10.4914 6.5023 10.6 7.04845 10.6 7.6V9.4H13L9.4 13L5.8 9.4H8.2V7.6C8.2 7.36362 8.15344 7.12956 8.06298 6.91117C7.97253 6.69278 7.83994 6.49435 7.67279 6.32721C7.50565 6.16006 7.30722 6.02748 7.08883 5.93702C6.87044 5.84656 6.63638 5.8 6.4 5.8H4.6V8.2L1 4.6L4.6 1V3.4H6.4Z' fill='white'/%3E%3Cpath d='M6.39999 5.2H3.99999V6.7L1.89999 4.6L4 2.5L3.99999 4H6.39999C6.87275 4 7.34088 4.09312 7.77766 4.27403C8.21443 4.45495 8.61129 4.72012 8.94558 5.05441C9.27987 5.38871 9.54505 5.78557 9.72596 6.22234C9.90688 6.65911 10 7.12724 10 7.6V10L11.5 10L9.4 12.1L7.3 10H8.8V7.6C8.8 7.28483 8.73792 6.97274 8.61731 6.68156C8.4967 6.39038 8.31991 6.1258 8.09705 5.90294C7.87419 5.68008 7.60962 5.5033 7.31844 5.38269C7.02725 5.26208 6.71517 5.2 6.39999 5.2Z' fill='black'/%3E%3C/g%3E%3C/svg%3E`;
  }

  private clearCursor() {
    const stage = this.getStage();
    if (stage) {
      this.setCursor(stage, "default");
    }
    this._cursorChange = false;
  }

  getNewBoxForKeepRatio(oldBox: Box, newBox: Box) {
    // Round input boxes to prevent accumulated precision errors
    const safeOldBox = roundBox(oldBox);
    const safeNewBox = roundBox(newBox);

    const activeAnchorName = this.getActiveAnchor();
    const movingVerticalCenter = activeAnchorName.includes("center");
    const movingHorizontalCenter = activeAnchorName.includes("middle");

    // Use the aspect ratio stored at transform start for consistency
    const aspectRatio = this.ratio || safeDivide(safeOldBox.width, safeOldBox.height, 1);

    // For all elements with side anchors, apply constraints with proper corner behavior
    // This works for both rotated and non-rotated elements (rotation=0 means no-op for rotate functions)
    if (movingVerticalCenter || movingHorizontalCenter) {
      // Use the same approach as the original working version - unrotate, apply logic, rotate back
      const stage = this.getStage();
      const mousePos = stage?.getPointerPosition();
      const anchorPos = this.findOne(`.${activeAnchorName}`)?.getAbsolutePosition();

      // Get the center point which will be our rotation pivot
      const centerX = safeOldBox.x + safeOldBox.width / 2;
      const centerY = safeOldBox.y + safeOldBox.height / 2;

      let isTopHalf = false;
      let isLeftHalf = false;

      if (mousePos && anchorPos) {
        // Transform mouse position into unrotated space using the rotate function
        const [unrotatedMouseX, unrotatedMouseY] = rotate(
          mousePos.x,
          mousePos.y,
          -safeOldBox.rotation,
          centerX,
          centerY
        );
        const [unrotatedAnchorX, unrotatedAnchorY] = rotate(
          anchorPos.x,
          anchorPos.y,
          -safeOldBox.rotation,
          centerX,
          centerY
        );

        isTopHalf = unrotatedMouseY < unrotatedAnchorY;
        isLeftHalf = unrotatedMouseX < unrotatedAnchorX;
      }

      // First unrotate the boxes to work in axis-aligned coordinates
      const oldUnrotated = { ...safeOldBox };
      const newUnrotated = { ...safeNewBox };

      // Unrotate both boxes around their center using the rotate function
      [oldUnrotated.x, oldUnrotated.y] = rotate(safeOldBox.x, safeOldBox.y, -safeOldBox.rotation, centerX, centerY);
      [newUnrotated.x, newUnrotated.y] = rotate(safeNewBox.x, safeNewBox.y, -safeOldBox.rotation, centerX, centerY);

      if (movingVerticalCenter) {
        // When moving vertical center, adjust width based on height change
        const heightChange = newUnrotated.height - oldUnrotated.height;
        newUnrotated.width = roundToPrecision(oldUnrotated.width + heightChange * aspectRatio);

        if (newUnrotated.width < MinimumWidth) {
          newUnrotated.width = MinimumWidth;
          newUnrotated.height = roundToPrecision(
            oldUnrotated.height + safeDivide(MinimumWidth - oldUnrotated.width, aspectRatio, 0)
          );
        }

        // Apply corner-specific positioning logic
        if (activeAnchorName === "top-center") {
          // Keep bottom edge fixed
          newUnrotated.y = roundToPrecision(oldUnrotated.y + oldUnrotated.height - newUnrotated.height);
          if (isLeftHalf) {
            // Behave like top-left corner
            newUnrotated.x = roundToPrecision(oldUnrotated.x + (oldUnrotated.width - newUnrotated.width));
          } else {
            // Behave like top-right corner
            newUnrotated.x = oldUnrotated.x;
          }
        } else if (activeAnchorName === "bottom-center") {
          // Keep top edge fixed
          newUnrotated.y = oldUnrotated.y;
          if (isLeftHalf) {
            // Behave like bottom-left corner
            newUnrotated.x = roundToPrecision(oldUnrotated.x + (oldUnrotated.width - newUnrotated.width));
          } else {
            // Behave like bottom-right corner
            newUnrotated.x = oldUnrotated.x;
          }
        }
      } else if (movingHorizontalCenter) {
        // When moving horizontal center, adjust height based on width change
        const widthChange = newUnrotated.width - oldUnrotated.width;
        newUnrotated.height = roundToPrecision(oldUnrotated.height + safeDivide(widthChange, aspectRatio, 0));

        if (newUnrotated.height < MinimumHeight) {
          newUnrotated.height = MinimumHeight;
          newUnrotated.width = roundToPrecision(
            oldUnrotated.width + (MinimumHeight - oldUnrotated.height) * aspectRatio
          );
        }

        // Apply corner-specific positioning logic
        if (activeAnchorName === "middle-left") {
          // Keep right edge fixed
          newUnrotated.x = roundToPrecision(oldUnrotated.x + oldUnrotated.width - newUnrotated.width);
          if (isTopHalf) {
            // Behave like top-left corner
            newUnrotated.y = roundToPrecision(oldUnrotated.y + (oldUnrotated.height - newUnrotated.height));
          } else {
            // Behave like bottom-left corner
            newUnrotated.y = oldUnrotated.y;
          }
        } else if (activeAnchorName === "middle-right") {
          // Keep left edge fixed
          newUnrotated.x = oldUnrotated.x;
          if (isTopHalf) {
            // Behave like top-right corner
            newUnrotated.y = roundToPrecision(oldUnrotated.y + (oldUnrotated.height - newUnrotated.height));
          } else {
            // Behave like bottom-right corner
            newUnrotated.y = oldUnrotated.y;
          }
        }
      }

      // Now rotate the result back using the rotate function
      [newBox.x, newBox.y] = rotate(newUnrotated.x, newUnrotated.y, safeOldBox.rotation, centerX, centerY);
      newBox.width = roundToPrecision(newUnrotated.width);
      newBox.height = roundToPrecision(newUnrotated.height);
      newBox.rotation = safeOldBox.rotation;
    }
  }

  private isAllTexts() {
    return this.uniformType == consts.CANVAS_ELEMENTS.TEXT_BLOCK;
  }

  private customizeTransformerBorder() {
    // Clean up any existing custom borders first
    const existingCustomBorders = this.find(".custom-border");
    for (const border of existingCustomBorders.toArray()) {
      border.destroy();
    }

    // Only customize border if borderEnabled is true
    if (!this.borderEnabled()) {
      return;
    }

    // Try to modify the transformer border to remove the rotation line segment
    const backBorder = this.findOne(".back") || this.find((node: Konva.Node) => node.name() === "back")[0];
    if (backBorder) {
      // Hide the original back border
      backBorder.visible(false);

      // Create a clean custom border using config values
      const rect = this._getNodeRect();
      const customBorder = new Konva.Rect({
        name: "custom-border", // Add name for cleanup
        x: 0,
        y: 0,
        width: rect.width,
        height: rect.height,
        stroke: this.borderStroke(), // Use config border stroke
        strokeWidth: this.borderStrokeWidth(), // Use config border width
        dash: this.borderDash(), // Use config border dash
        fill: "transparent",
        listening: false,
      });

      // Add the custom border and move it to bottom
      this.add(customBorder);
      customBorder.moveToBottom();
    }
  }

  /*
   * Konva calls this function with the old bounding box and new bounding box on each transform change.
   * We override it to add snapping functionality.
   * The function is called with the bounding box in screen coordinates (as opposed to canvas/stage coordinates)
   * For each box object, we have {x,y,width,height,rotation}
   * (x,y) - for top-left
   * (width,height) - size of box
   * rotation - rotation in radians
   */
  boundBoxFuncWithSnap(oldBox: Box, newBox: Box) {
    const activeAnchorName = this.getActiveAnchor();

    if (activeAnchorName == "rotater") {
      if (this.lockRotationToIntervals) {
        this.rotationSnaps([0, 45, 90, 135, 180, 225, 270, 315]);
        if (Math.abs(newBox.rotation % (Math.PI / 4)) > 0) {
          return oldBox;
        }
      } else {
        this.rotationSnaps([0, 90, 180, 270]);
      }
      return newBox;
    }

    // we don't allow flipping the shape
    if (newBox.width < 0 || newBox.height < 0) return oldBox;

    const corner = isCorner(activeAnchorName);
    const movingVerticalCenter = activeAnchorName.includes("center");
    const movingHorizontalCenter = activeAnchorName.includes("middle");

    const keepProportion = this.keepRatio() || this.keepAspectRatio;

    // special case for images: we don't want to limit their height

    const allTexts = this.isAllTexts();
    // texts are ofter long and thin, so we don't want to limit their height.
    // for all other elements we limit the resize to 30 pixels (user should zoom in if she wants smaller)

    const specialCaseForImages =
      !allTexts && !corner && this.keepRatio() && (movingVerticalCenter || movingHorizontalCenter);

    if (!allTexts) {
      if (specialCaseForImages) {
        this.getNewBoxForKeepRatio(oldBox, newBox);
      } else {
        if (Math.abs(oldBox.width - newBox.width) > 1e-3 && newBox.width < MinimumWidth) {
          // Recalculate position from scratch to keep opposite corner/edge fixed
          const clampedWidth = MinimumWidth;
          const clampedHeight = keepProportion ? (MinimumWidth * oldBox.height) / oldBox.width : newBox.height;

          if (activeAnchorName.includes("top") && activeAnchorName.includes("left")) {
            // top-left moving: keep bottom-right fixed
            const brX = oldBox.x + oldBox.width * Math.cos(oldBox.rotation) - oldBox.height * Math.sin(oldBox.rotation);
            const brY = oldBox.y + oldBox.width * Math.sin(oldBox.rotation) + oldBox.height * Math.cos(oldBox.rotation);
            newBox.x = brX - clampedWidth * Math.cos(oldBox.rotation) + clampedHeight * Math.sin(oldBox.rotation);
            newBox.y = brY - clampedWidth * Math.sin(oldBox.rotation) - clampedHeight * Math.cos(oldBox.rotation);
          } else if (activeAnchorName.includes("top") && activeAnchorName.includes("right")) {
            // top-right moving: keep bottom-left fixed (the top-left corner)
            newBox.x = oldBox.x;
            newBox.y = oldBox.y;
          } else if (activeAnchorName.includes("bottom") && activeAnchorName.includes("left")) {
            // bottom-left moving: keep top-right fixed
            const trX = oldBox.x + oldBox.width * Math.cos(oldBox.rotation);
            const trY = oldBox.y + oldBox.width * Math.sin(oldBox.rotation);
            newBox.x = trX - clampedWidth * Math.cos(oldBox.rotation);
            newBox.y = trY - clampedWidth * Math.sin(oldBox.rotation);
          } else if (activeAnchorName.includes("bottom") && activeAnchorName.includes("right")) {
            // bottom-right moving: keep top-left fixed
            newBox.x = oldBox.x;
            newBox.y = oldBox.y;
          } else if (activeAnchorName.includes("middle-left") || activeAnchorName.includes("left")) {
            // left edge moving: keep right edge fixed
            // Right edge center in absolute coords = (oldBox.x, oldBox.y) + (oldBox.width, oldBox.height/2) rotated
            const rightCenterX =
              oldBox.x + oldBox.width * Math.cos(oldBox.rotation) - (oldBox.height / 2) * Math.sin(oldBox.rotation);
            const rightCenterY =
              oldBox.y + oldBox.width * Math.sin(oldBox.rotation) + (oldBox.height / 2) * Math.cos(oldBox.rotation);
            // New left edge center = right edge center - (new width, 0) rotated
            const leftCenterX = rightCenterX - clampedWidth * Math.cos(oldBox.rotation);
            const leftCenterY = rightCenterY - clampedWidth * Math.sin(oldBox.rotation);
            // New top-left = left edge center - (0, new height/2) rotated
            newBox.x = leftCenterX + (clampedHeight / 2) * Math.sin(oldBox.rotation);
            newBox.y = leftCenterY - (clampedHeight / 2) * Math.cos(oldBox.rotation);
          } else if (activeAnchorName.includes("middle-right") || activeAnchorName.includes("right")) {
            // right edge moving: keep left edge fixed (top-left corner)
            newBox.x = oldBox.x;
            newBox.y = oldBox.y;
          }

          newBox.width = clampedWidth;
          newBox.height = clampedHeight;
        }
        if (Math.abs(oldBox.height - newBox.height) > 1e-3 && newBox.height < MinimumHeight) {
          // For rotated shapes hitting minimum size, we need to recalculate position from scratch
          // to keep the opposite corner/edge fixed, rather than adjusting the already-wrong newBox position

          // Calculate the dimensions we'll have after clamping
          const clampedHeight = MinimumHeight;
          const clampedWidth = keepProportion ? (MinimumHeight * oldBox.width) / oldBox.height : newBox.width;

          // For each anchor, determine which corner/edge should stay fixed and calculate new position
          if (activeAnchorName.includes("top") && activeAnchorName.includes("left")) {
            // top-left moving: keep bottom-right fixed
            // Bottom-right in absolute coords = (oldBox.x, oldBox.y) + (oldBox.width, oldBox.height) rotated
            const brX = oldBox.x + oldBox.width * Math.cos(oldBox.rotation) - oldBox.height * Math.sin(oldBox.rotation);
            const brY = oldBox.y + oldBox.width * Math.sin(oldBox.rotation) + oldBox.height * Math.cos(oldBox.rotation);
            // New top-left = bottom-right - (new width, new height) rotated
            newBox.x = brX - clampedWidth * Math.cos(oldBox.rotation) + clampedHeight * Math.sin(oldBox.rotation);
            newBox.y = brY - clampedWidth * Math.sin(oldBox.rotation) - clampedHeight * Math.cos(oldBox.rotation);
          } else if (activeAnchorName.includes("top") && activeAnchorName.includes("right")) {
            // top-right moving: keep bottom-left fixed (which is the top-left corner, oldBox.x, oldBox.y)
            // New position stays at oldBox position since bottom-left IS the top-left
            newBox.x = oldBox.x;
            newBox.y = oldBox.y;
          } else if (activeAnchorName.includes("bottom") && activeAnchorName.includes("left")) {
            // bottom-left moving: keep top-right fixed
            // Top-right in absolute coords = (oldBox.x, oldBox.y) + (oldBox.width, 0) rotated
            const trX = oldBox.x + oldBox.width * Math.cos(oldBox.rotation);
            const trY = oldBox.y + oldBox.width * Math.sin(oldBox.rotation);
            // New top-left = top-right - (new width, 0) rotated
            newBox.x = trX - clampedWidth * Math.cos(oldBox.rotation);
            newBox.y = trY - clampedWidth * Math.sin(oldBox.rotation);
          } else if (activeAnchorName.includes("bottom") && activeAnchorName.includes("right")) {
            // bottom-right moving: keep top-left fixed
            // Position doesn't change
            newBox.x = oldBox.x;
            newBox.y = oldBox.y;
          } else if (
            activeAnchorName.includes("top-center") ||
            (activeAnchorName.includes("top") &&
              !activeAnchorName.includes("left") &&
              !activeAnchorName.includes("right"))
          ) {
            // top edge moving: keep bottom edge fixed
            // Bottom edge center in absolute coords = (oldBox.x, oldBox.y) + (oldBox.width/2, oldBox.height) rotated
            const bottomCenterX =
              oldBox.x + (oldBox.width / 2) * Math.cos(oldBox.rotation) - oldBox.height * Math.sin(oldBox.rotation);
            const bottomCenterY =
              oldBox.y + (oldBox.width / 2) * Math.sin(oldBox.rotation) + oldBox.height * Math.cos(oldBox.rotation);
            // New top edge center = bottom edge center - (0, new height) rotated
            const topCenterX = bottomCenterX + clampedHeight * Math.sin(oldBox.rotation);
            const topCenterY = bottomCenterY - clampedHeight * Math.cos(oldBox.rotation);
            // New top-left = top edge center - (new width/2, 0) rotated
            newBox.x = topCenterX - (clampedWidth / 2) * Math.cos(oldBox.rotation);
            newBox.y = topCenterY - (clampedWidth / 2) * Math.sin(oldBox.rotation);
          } else if (
            activeAnchorName.includes("bottom-center") ||
            (activeAnchorName.includes("bottom") &&
              !activeAnchorName.includes("left") &&
              !activeAnchorName.includes("right"))
          ) {
            // bottom edge moving: keep top edge fixed (top-left corner)
            newBox.x = oldBox.x;
            newBox.y = oldBox.y;
          }

          newBox.width = clampedWidth;
          newBox.height = clampedHeight;
        }
      }
    }

    if (!this.shouldSnap() && !this.shouldShowGuidelines()) {
      clearGuidelines(this.overlayLayer, "transformer-snap-guide");
      return newBox;
    }

    const scale = this.getStage()!.scaleX();
    const pos = this.getStage()!.position();
    const posScale = { x: pos.x, y: pos.y, scale };

    function getAnchorPosInStage(box: any, anchor: string, opposite = false) {
      return viewportToStage(posScale, getAnchorPos(anchor, box, opposite));
    }

    // get position of the dragged anchor in stage coordinates, not screen
    const { x, y } = getAnchorPosInStage(newBox, activeAnchorName);

    // these are for the new coordinate (snapped coordinate)
    let snappedX = x,
      snappedY = y;

    // find possible snap lines
    const { horizSnap: ySnap, vertSnap: xSnap } = checkTransformSnapLines(
      { ...viewportToStage(posScale, newBox), x, y },
      this.snapData!,
      this.getActiveAnchor(),
      this.centered
    );

    // decide if we should use them
    const shouldSnapX = xSnap && xSnap.distance < consts.SNAPPING_DISTANCE;
    const shouldSnapY = ySnap && ySnap.distance < consts.SNAPPING_DISTANCE;

    // if no good snapping candidate found, return the intended box
    if (!shouldSnapY && !shouldSnapX) {
      clearGuidelines(this.overlayLayer, "transformer-snap-guide");
      return newBox;
    }

    // we don't do snapping for middle anchors when rotated. (it's tricky, leaving it for now)
    if (newBox.rotation && !corner) {
      clearGuidelines(this.overlayLayer, "transformer-snap-guide");
      return newBox;
    }

    // we're going to need the opposite anchor from the one we're dragging.
    // The opposite corner always stays in place.
    // note: in 'centered' mode the pivot is the center of the square.
    let { x: px, y: py } = getAnchorPosInStage(newBox, activeAnchorName, true);

    if (keepProportion) {
      // when we transform and keep aspect ratio the corner must move along the line
      // from the opposite corner to itself (itself means the original corner, not the x,y I calculated above)
      // to snap while keeping aspect ratio, we will snap to the closest target line (x or y),
      // and we move the other coordinate to match the aspect-ratio.
      // that means finding the intersection of 2 lines:
      // the snap line and the diagonal.

      // The next if statement will fail if xSnap or ySnap is null, since it compares the distance.
      if (!xSnap || !ySnap) return newBox;

      const diagonalX = x - px;
      const diagonalY = y - py;
      if (ySnap.distance < xSnap.distance) {
        snappedY = ySnap.coord;
        if (diagonalY != 0) {
          const t = (snappedY - py) / diagonalY;
          snappedX = px + diagonalX * t;
        }
      } else {
        snappedX = xSnap.coord;
        if (diagonalX != 0) {
          const t = (snappedX - px) / diagonalX;
          snappedY = py + diagonalY * t;
        }
      }
    } else {
      if (shouldSnapX) snappedX = xSnap.coord;
      if (shouldSnapY) snappedY = ySnap.coord;
    }

    // if we're in centered mode (it's really symmetrical transform),
    // the opposite corner also moved and we're adjusting it to match.
    if (this.centered) {
      px += x - snappedX;
      py += y - snappedY;
    }

    // so now we've snapped the anchor, and its opposite anchor.
    // we should calculate the new box of the transformer.
    // if we have 2 opposite corners of an axis-aligned box it's easy to calculate top-left and width and height.
    // But me might be rotated, so we should reverse the rotation first and become axis-aligned
    // We'll rotate the anchor that moves around its opposite anchor (which stays fixed in place),
    const [x2, y2] = rotate(snappedX, snappedY, -oldBox.rotation, px, py);

    // Now we'll calculate the new box.
    // We start with what Konva gave us, just convert it to stage coordinates
    const newRect = viewportToStage(posScale, newBox);

    // The first thing is to change width and height.
    // Note: If we're moving a corner anchor both width and height change,
    // but if we're moving the middle anchors only one of them changes.
    // IMPORTANT: If keepProportion is true and we already called getNewBoxForKeepRatio,
    // we should preserve those dimensions instead of recalculating
    if (specialCaseForImages) {
      // Keep the dimensions that were already calculated with correct aspect ratio
      // newRect already has the correct dimensions from newBox via viewportToStage
    } else {
      if (!movingVerticalCenter) {
        newRect.width = Math.abs(x2 - px);
      }
      if (!movingHorizontalCenter) {
        newRect.height = Math.abs(y2 - py);
      }
    }

    // Now we change the top-left corner. Again, if we're moving a corner anchor both x and y change,
    // but when moving a middle anchor only one of them changes.
    let topleft_x = movingVerticalCenter ? newRect.x : Math.min(x2, px);
    let topleft_y = movingHorizontalCenter ? newRect.y : Math.min(y2, py);

    // When keeping proportion with side anchors, we need to adjust position based on which corner we're simulating
    if (specialCaseForImages) {
      // Get the position that was already calculated in getNewBoxForKeepRatio
      topleft_x = newRect.x;
      topleft_y = newRect.y;
    }

    // must not forget to rotate back to the original rotation
    [newRect.x, newRect.y] = rotate(topleft_x, topleft_y, oldBox.rotation, px, py);

    // Only now after final snapping we can show guidelines, for the updated point
    if (this.shouldShowGuidelines() && (shouldSnapX || shouldSnapY)) {
      const guides = getSnapLinesForPoint({ x: snappedX, y: snappedY }, this.snapData!);
      const sizeGuides = getSizeSnapLines(newRect, this.snapData!, this.getActiveAnchor());
      renderGuidelinesDirectly(this.overlayLayer, guides, sizeGuides, posScale.scale, "transformer-snap-guide");
    } else {
      clearGuidelines(this.overlayLayer, "transformer-snap-guide");
    }

    // we check if we need to snap - text elements don't snap but do show guidelines.
    // if we do, we convert the box we calculated to screen coordinates, as Konva expects
    return this.shouldSnap() ? stageToViewport(posScale, newRect) : newBox;
  }
}
// @ts-expect-error Konva types are mismatched here.
Konva["SnappingTransformer"] = SnappingTransformer;

// @ts-expect-error Konva types are mismatched here.
export default "SnappingTransformer" as KonvaNodeComponent<Konva.Transformer, SnappingTransformerProps>;
